import { useCallback, useMemo, useState } from 'react';

import { useIsFetching } from '@tanstack/react-query';

import { reservationQueryKeys } from '~/hooks/apis/reservations/reservationQueryKeys';
import {
  type ExtraGuestSurchargeWrite,
  useApplyExtraGuestSurcharges,
} from '~/hooks/apis/reservations/useApplyExtraGuestSurcharges';

import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import {
  EMPTY_GATE_RESOLUTION,
  type EgsArchiveDecision,
  type EgsGateResolution,
  buildEgsApplyPlans,
  buildEgsPayload,
  getNothingToApplyReason,
  isApplicable,
  isWaitingOnGate,
} from './buildEgsApplyPlan';
import { buildDefaultSelectedRoomRateIds, buildEgsApplyCandidates } from './egsApplyCandidates';
import { buildEgsRatePlans, filterSelectionsByRatePlan, getDefaultEgsRatePlanId } from './egsRatePlans';
import type { EgsRoomMatch } from './egsRoomMatcher';
import type { EgsGuestSlot, EgsRoomGroup } from './types';

export function useEgsApplyPlan({
  egsByRoomRateId,
  matchByRoomLabel,
  onApplied,
  propertyId,
  roomGroups,
  roomRateSelections,
}: {
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  matchByRoomLabel: Record<string, EgsRoomMatch>;
  onApplied: () => void;
  propertyId: string;
  roomGroups: Array<EgsRoomGroup>;
  roomRateSelections: Array<RoomRateSelection>;
}) {
  const applyExtraGuestSurcharges = useApplyExtraGuestSurcharges(propertyId);

  /**
   * Until the invalidated records refetch, a room rate that was just created still reads as `create` and
   * re-sending would write a second record, so the reload counts as still applying.
   */
  const isReloadingExistingRecords = useIsFetching({
    queryKey: reservationQueryKeys.extraGuestSurchargesByRoomRate(propertyId, []),
  });

  const ratePlans = useMemo(() => buildEgsRatePlans(roomRateSelections), [roomRateSelections]);

  const [ratePlanId, setRatePlanId] = useState<string | undefined>(() => getDefaultEgsRatePlanId(ratePlans));

  const scopedRoomRateSelections = useMemo(
    () => filterSelectionsByRatePlan(roomRateSelections, ratePlanId),
    [ratePlanId, roomRateSelections],
  );

  const buildCandidatesFor = useCallback(
    (selections: Array<RoomRateSelection>) =>
      buildEgsApplyCandidates({ matchByRoomLabel, roomGroups, roomRateSelections: selections }),
    [matchByRoomLabel, roomGroups],
  );

  const candidates = useMemo(
    () => buildCandidatesFor(scopedRoomRateSelections),
    [buildCandidatesFor, scopedRoomRateSelections],
  );

  // Seeded once rather than synced, so re-deriving candidates cannot discard ticks the CM already made.
  const [selectedRoomRateIdsByRoomLabel, setSelectedRoomRateIdsByRoomLabel] = useState<Record<string, Array<string>>>(
    () => buildDefaultSelectedRoomRateIds(candidates),
  );

  const selectionsByRoomLabel = useMemo(() => {
    return Object.fromEntries(
      candidates.map((candidate) => {
        const selectedIds = new Set(selectedRoomRateIdsByRoomLabel[candidate.roomLabel] ?? []);

        return [
          candidate.roomLabel,
          candidate.candidates.filter((selection) => selectedIds.has(String(selection.roomRate.id))),
        ];
      }),
    );
  }, [candidates, selectedRoomRateIdsByRoomLabel]);

  const [archiveByRowId, setArchiveByRowId] = useState<Record<string, EgsArchiveDecision>>({});
  const [gateByRoomRateId, setGateByRoomRateId] = useState<Record<string, EgsGateResolution>>({});
  const [hasDiscardedGateAnswers, setHasDiscardedGateAnswers] = useState(false);

  /**
   * An answer names two specific rows, so a decision that outlives the mapping it was given for waves through
   * a conflict nobody looked at. Called from the setters that move the mapping rather than an effect watching
   * it, so no render survives between the tick and the clear.
   */
  const clearGateAnswers = () => {
    if (Object.keys(archiveByRowId).length > 0 || Object.keys(gateByRoomRateId).length > 0) {
      setHasDiscardedGateAnswers(true);
    }

    setArchiveByRowId({});
    setGateByRoomRateId({});
  };

  const plans = useMemo(
    () => buildEgsApplyPlans({ archiveByRowId, egsByRoomRateId, gateByRoomRateId, roomGroups, selectionsByRoomLabel }),
    [archiveByRowId, egsByRoomRateId, gateByRoomRateId, roomGroups, selectionsByRoomLabel],
  );

  // Built at full reach, since asking a CM to tick rooms is only honest if some answer writes.
  const nothingToApplyReason = useMemo(
    () =>
      getNothingToApplyReason(
        buildEgsApplyPlans({
          egsByRoomRateId,
          roomGroups,
          selectionsByRoomLabel: Object.fromEntries(
            candidates.map((candidate) => [candidate.roomLabel, candidate.candidates]),
          ),
        }),
      ),
    [candidates, egsByRoomRateId, roomGroups],
  );

  const applicablePlans = useMemo(() => plans.filter(isApplicable), [plans]);
  const blockedPlans = useMemo(() => plans.filter((plan) => !!plan.blocker && !isWaitingOnGate(plan)), [plans]);
  const gatedPlans = useMemo(() => plans.filter(isWaitingOnGate), [plans]);

  /**
   * The snackbar names one failure and then goes, so the last attempt's failures stay readable here for as
   * long as the CM is still in the wizard. Firing the mutation again clears `data`, so a retry empties this
   * on its own rather than showing the previous attempt's list beside an in-flight one.
   */
  const failedWrites = useMemo(
    () => (applyExtraGuestSurcharges.data ?? []).filter((result) => !result.succeeded),
    [applyExtraGuestSurcharges.data],
  );

  const toggleRoomRate = (roomLabel: string, roomRateId: string, checked: boolean) => {
    setSelectedRoomRateIdsByRoomLabel((current) => {
      const selectedIds = new Set(current[roomLabel] ?? []);

      if (checked) {
        selectedIds.add(roomRateId);
      } else {
        selectedIds.delete(roomRateId);
      }

      return { ...current, [roomLabel]: Array.from(selectedIds) };
    });
    clearGateAnswers();
  };

  // A tick on one plan names a room rate the next does not have, so ticks are seeded fresh, never carried.
  const selectRatePlan = (nextRatePlanId: string) => {
    if (nextRatePlanId === ratePlanId) {
      return;
    }

    setRatePlanId(nextRatePlanId);
    setSelectedRoomRateIdsByRoomLabel(
      buildDefaultSelectedRoomRateIds(
        buildCandidatesFor(filterSelectionsByRatePlan(roomRateSelections, nextRatePlanId)),
      ),
    );
    clearGateAnswers();
  };

  // Answering again is what makes the notice stale, so each of the three setters retires it.
  const setArchiveDecision = (rowId: string, decision: EgsArchiveDecision) => {
    setHasDiscardedGateAnswers(false);
    setArchiveByRowId((current) => ({ ...current, [rowId]: decision }));
  };

  const setSlotWinner = (roomRateId: string, slot: EgsGuestSlot, rowId: string) => {
    setHasDiscardedGateAnswers(false);
    setGateByRoomRateId((current) => {
      const resolution = current[roomRateId] ?? EMPTY_GATE_RESOLUTION;

      return {
        ...current,
        [roomRateId]: { ...resolution, slotWinnerRowIds: { ...resolution.slotWinnerRowIds, [slot]: rowId } },
      };
    });
  };

  const setPayableAtProperty = (roomRateId: string, payableAtProperty: boolean) => {
    setHasDiscardedGateAnswers(false);
    setGateByRoomRateId((current) => ({
      ...current,
      [roomRateId]: { ...EMPTY_GATE_RESOLUTION, ...current[roomRateId], payableAtProperty },
    }));
  };

  const handleApply = () => {
    const writes: Array<ExtraGuestSurchargeWrite> = applicablePlans.map((plan) => ({
      operation: plan.operation,
      payload: buildEgsPayload(plan),
      // The room rate is what failed, so name it the way the CM sees it rather than by Salesforce record.
      roomLabel: `${plan.roomTypeName} · ${plan.ratePlanName}`,
      roomRateId: plan.roomRateId,
      roomTypeId: plan.roomTypeId,
    }));

    // Stays open when anything failed, so a retry is targeted: once the invalidated query refetches, the
    // room rates that saved read as unchanged and drop out, leaving only the failures to send again.
    applyExtraGuestSurcharges.mutate(writes, {
      onSuccess: (results) => {
        if (results.every((result) => result.succeeded)) {
          onApplied();
        }
      },
    });
  };

  return {
    applicablePlans,
    blockedPlans,
    candidates,
    failedWrites,
    gatedPlans,
    handleApply,
    hasDiscardedGateAnswers,
    isApplying: applyExtraGuestSurcharges.isPending || isReloadingExistingRecords > 0,
    nothingToApplyReason,
    plans,
    ratePlanId,
    ratePlans,
    selectRatePlan,
    selectedRoomRateIdsByRoomLabel,
    setArchiveDecision,
    setPayableAtProperty,
    setSlotWinner,
    toggleRoomRate,
  };
}
